/**
 * TanStack Query hooks for the admin need-mappings surface — PIM Wave P4
 * Task 7. Mirrors `attribute-fields/hooks.ts`'s field-GROUPS shape (a small,
 * un-paginated `useQuery` list + a single prefix-invalidate helper shared by
 * every mutation) rather than the fields/suggestions `useInfiniteQuery`
 * shape — `GET /admin/catalog/need-mappings` returns the WHOLE list in one
 * response (backend doc comment: "a small, admin-curated set"), so there is
 * no cursor to page through and no filters to key the list on.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createNeedMapping,
  deleteNeedMapping,
  listNeedMappings,
  needMappingKeys,
  updateNeedMapping,
  type NeedMapping,
  type NeedMappingCreate,
  type NeedMappingListResponse,
  type NeedMappingUpdate,
} from "@/features/admin/need-mappings/api";

export function useNeedMappings(): UseQueryResult<NeedMappingListResponse, Error> {
  return useQuery<NeedMappingListResponse, Error>({
    queryKey: needMappingKeys.list(),
    queryFn: () => listNeedMappings(),
    staleTime: 10_000,
  });
}

function useInvalidateAfterNeedMappingWrite(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: needMappingKeys.list() });
  };
}

export function useCreateNeedMapping(): UseMutationResult<
  NeedMapping,
  Error,
  NeedMappingCreate
> {
  const invalidate = useInvalidateAfterNeedMappingWrite();
  return useMutation<NeedMapping, Error, NeedMappingCreate>({
    mutationFn: (body) => createNeedMapping(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateNeedMapping(): UseMutationResult<
  NeedMapping,
  Error,
  { id: string; body: NeedMappingUpdate }
> {
  const invalidate = useInvalidateAfterNeedMappingWrite();
  return useMutation<NeedMapping, Error, { id: string; body: NeedMappingUpdate }>({
    mutationFn: ({ id, body }) => updateNeedMapping(id, body),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteNeedMapping(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateAfterNeedMappingWrite();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteNeedMapping(id),
    onSuccess: () => invalidate(),
  });
}
